import { Typography } from '@capsule/consumer-design-system';

import type { FormikProps } from 'formik';
import { Form } from 'formik';
import { css } from 'styled-components';

import { MedicationSearchSource, useSearchMedication } from 'src/app/hooks/data/medication';
import type { MedicationSearchResult } from 'src/app/types';
import { FORM_INPUT_ICON_STRINGS } from 'src/consts';

import { AutoCompleteInputWired } from '../../Form/AutocompleteInput/container';
import { MedicationChip } from '../../PageSections/DoctorTransferForm/MedicationsFieldArray/MedicationChip';
import {
  MedicationChipContainer,
  MedicationsChipListContainer,
} from '../../PageSections/DoctorTransferForm/MedicationsFieldArray/MedicationChip/styles';
import { MedSearchItem } from '../../PageSections/DoctorTransferForm/MedicationsFieldArray/styles';

import type { MedicationsFormValueType } from './MedicationPicker';
import { GreenFontWithRedError, StyledForm } from './styles';

export const MedicationSearchPicker = (props: {
  formik: FormikProps<MedicationsFormValueType>;
  zipCode: string;
  formId: string;
  hasAnyFormMeds: boolean;
}) => {
  const { zipCode, formId, hasAnyFormMeds } = props;
  const { mutateAsync: searchMedication } = useSearchMedication();

  const id = 'searchMed';

  // The type annotation here is rather stupid, but it satisfies the AutoCompleteInputWired interface
  const itemToString = (item: Record<string, any>) => {
    const typedItem = item as MedicationSearchResult;
    return typedItem?.primaryName;
  };

  const MenuItem = ({ item }: { item: Record<string, any> }) => {
    const typedItem = item as MedicationSearchResult;
    return <MedSearchItem>{typedItem.primaryName}</MedSearchItem>;
  };

  const { setFieldValue, values, errors, submitCount } = props.formik;

  const { otherMeds } = values;

  return (
    <GreenFontWithRedError hasError={!!errors?.form && submitCount > 0}>
      <Typography
        // Offset the spacing of the form label, which this component doesn't show
        css={css`
          margin-bottom: -${({ theme }) => theme.space.s5};
        `}
        variant="overline"
      >
        {`Add ${hasAnyFormMeds ? 'other' : ''} medications`}
      </Typography>
      <StyledForm id={formId} as={Form}>
        <AutoCompleteInputWired
          showLoader={true}
          id={id}
          name={id}
          aria-label={'Search a medication name'}
          placeholder={`example: "Sertraline" or "Zoloft"`}
          searchHelperText={'Search a medication name'}
          itemToString={itemToString}
          renderMenuItem={MenuItem}
          dispatchSegmentTrack={() => undefined}
          fetchItemsOnChange={async input => {
            if (input.length > 2) {
              const medications = await searchMedication({
                zipCode,
                searchTerm: input,
                source: MedicationSearchSource.healthProfileForm,
              });
              return medications;
            }
          }}
          onSelectedItemChangeCallback={item => {
            setFieldValue('searchMed', '');
            setFieldValue('otherMeds', otherMeds ? [...otherMeds, item] : [item]);
          }}
          iconString={FORM_INPUT_ICON_STRINGS.system_search}
          disabled={false}
          debounceWaitTime={200}
          isTransferForm={false}
        />

        <MedicationsChipListContainer>
          {otherMeds &&
            Object.entries(otherMeds)
              .filter(([, med]) => !!med)
              .map(([key, med]) => {
                const primaryName = med?.primaryName ?? key;

                return (
                  <MedicationChipContainer key={primaryName}>
                    <MedicationChip
                      title={primaryName}
                      onClick={() =>
                        setFieldValue(
                          'otherMeds',
                          otherMeds.filter((item: MedicationSearchResult) => item.primaryName !== primaryName)
                        )
                      }
                    />
                  </MedicationChipContainer>
                );
              })}
        </MedicationsChipListContainer>
      </StyledForm>
    </GreenFontWithRedError>
  );
};
